'use client'

import { Combobox as ComboboxPrimitive } from '@base-ui/react'
import * as React from 'react'
import { LuCheck, LuChevronDown, LuX } from 'react-icons/lu'

import { Button } from '@/components/ui/button'
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from '@/components/ui/input-group'
import { cn } from '@/lib/utils'

const Combobox = ComboboxPrimitive.Root

function ComboboxValue({ ...props }: ComboboxPrimitive.Value.Props) {
  return <ComboboxPrimitive.Value data-slot="combobox-value" {...props} />
}

function ComboboxTrigger({
  className,
  children,
  ...props
}: ComboboxPrimitive.Trigger.Props) {
  return (
    <ComboboxPrimitive.Trigger
      data-slot="combobox-trigger"
      className={cn("[&_svg:not([class*='size-'])]:size-4", className)}
      {...props}
    >
      {children}
      <LuChevronDown
        data-slot="combobox-trigger-icon"
        className="pointer-events-none size-4 text-muted-foreground"
      />
    </ComboboxPrimitive.Trigger>
  )
}

function ComboboxClear({ className, ...props }: ComboboxPrimitive.Clear.Props) {
  return (
    <ComboboxPrimitive.Clear
      data-slot="combobox-clear"
      render={<InputGroupButton variant="ghost" size="icon-xs" />}
      className={cn(className)}
      {...props}
    >
      <LuX className="pointer-events-none" />
    </ComboboxPrimitive.Clear>
  )
}

function ComboboxInput({
  className,
  children,
  disabled = false,
  showTrigger = true,
  showClear = false,
  ...props
}: ComboboxPrimitive.Input.Props & {
  showTrigger?: boolean
  showClear?: boolean
}) {
  return (
    <InputGroup className={cn('w-auto', className)}>
      <ComboboxPrimitive.Input
        render={<InputGroupInput disabled={disabled} />}
        {...props}
      />
      <InputGroupAddon align="inline-end">
        {showTrigger && (
          <InputGroupButton
            size="icon-xs"
            variant="ghost"
            asChild
            data-slot="input-group-button"
            className="group-has-data-[slot=combobox-clear]/input-group:hidden
              data-pressed:bg-transparent"
            disabled={disabled}
          >
            <ComboboxTrigger />
          </InputGroupButton>
        )}
        {showClear && <ComboboxClear disabled={disabled} />}
      </InputGroupAddon>
      {children}
    </InputGroup>
  )
}

function ComboboxContent({
  className,
  side = 'bottom',
  sideOffset = 6,
  align = 'start',
  alignOffset = 0,
  anchor,
  container,
  ...props
}: ComboboxPrimitive.Popup.Props &
  Pick<
    ComboboxPrimitive.Positioner.Props,
    'side' | 'align' | 'sideOffset' | 'alignOffset' | 'anchor'
  > &
  // Where to portal the popup, when <body> won't do. A Radix Dialog traps
  // focus: a `focusin` on anything its content doesn't contain is pulled
  // straight back (see @radix-ui/react-focus-scope), so a popup left on <body>
  // has inputs that can be clicked but never typed into. Portalling into the
  // dialog puts them inside the trap. See `useDialogPortalContainer`.
  Pick<ComboboxPrimitive.Portal.Props, 'container'>) {
  return (
    <ComboboxPrimitive.Portal container={container}>
      <ComboboxPrimitive.Positioner
        side={side}
        sideOffset={sideOffset}
        align={align}
        alignOffset={alignOffset}
        anchor={anchor}
        // Base UI portals the popup to <body>. Radix's modal Dialog sets
        // `pointer-events: none` on <body> while open, which this popup would
        // otherwise inherit (Radix's own popovers re-enable it automatically,
        // Base UI's don't). Restore it so the dropdown stays interactive.
        className="pointer-events-auto isolate z-dropdown"
      >
        <ComboboxPrimitive.Popup
          data-slot="combobox-content"
          data-chips={!!anchor}
          className={cn(
            `group/combobox-content relative max-h-96 w-(--anchor-width)
            max-w-(--available-width)
            min-w-[calc(var(--anchor-width)+--spacing(7))]
            origin-(--transform-origin) overflow-hidden rounded-md bg-popover
            text-popover-foreground shadow-md ring-1 ring-foreground/10
            duration-100 data-[chips=true]:min-w-(--anchor-width)
            data-[side=bottom]:slide-in-from-top-2
            data-[side=left]:slide-in-from-right-2
            data-[side=right]:slide-in-from-left-2
            data-[side=top]:slide-in-from-bottom-2 *:data-[slot=input-group]:m-1
            *:data-[slot=input-group]:mb-0 *:data-[slot=input-group]:h-8
            *:data-[slot=input-group]:border-input/30
            *:data-[slot=input-group]:bg-input/30
            *:data-[slot=input-group]:shadow-none data-open:animate-in
            data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out
            data-closed:fade-out-0 data-closed:zoom-out-95`,
            className,
          )}
          {...props}
        />
      </ComboboxPrimitive.Positioner>
    </ComboboxPrimitive.Portal>
  )
}

function ComboboxList({ className, ...props }: ComboboxPrimitive.List.Props) {
  return (
    <ComboboxPrimitive.List
      data-slot="combobox-list"
      className={cn(
        `max-h-[min(calc(--spacing(96)---spacing(9)),calc(var(--available-height)---spacing(9)))]
        scroll-py-1 overflow-y-auto p-1 data-empty:p-0`,
        className,
      )}
      {...props}
    />
  )
}

function ComboboxItem({
  className,
  children,
  ...props
}: ComboboxPrimitive.Item.Props) {
  return (
    <ComboboxPrimitive.Item
      data-slot="combobox-item"
      className={cn(
        `relative flex w-full cursor-default items-center gap-2 rounded-sm
        py-1.5 pl-8 pr-2 text-sm outline-hidden select-none
        data-highlighted:bg-accent data-highlighted:text-accent-foreground
        data-disabled:pointer-events-none data-disabled:opacity-50
        [&_svg]:pointer-events-none [&_svg]:shrink-0
        [&_svg:not([class*='size-'])]:size-4`,
        className,
      )}
      {...props}
    >
      {children}
      <ComboboxPrimitive.ItemIndicator
        data-slot="combobox-item-indicator"
        render={
          <span
            className="pointer-events-none absolute left-2 flex size-4
              items-center justify-center"
          />
        }
      >
        <LuCheck className="pointer-events-none size-4 pointer-coarse:size-5" />
      </ComboboxPrimitive.ItemIndicator>
    </ComboboxPrimitive.Item>
  )
}

function ComboboxGroup({ className, ...props }: ComboboxPrimitive.Group.Props) {
  return (
    <ComboboxPrimitive.Group
      data-slot="combobox-group"
      className={cn(className)}
      {...props}
    />
  )
}

function ComboboxLabel({
  className,
  ...props
}: ComboboxPrimitive.GroupLabel.Props) {
  return (
    <ComboboxPrimitive.GroupLabel
      data-slot="combobox-label"
      className={cn(
        `px-2 py-1.5 text-xs text-muted-foreground pointer-coarse:px-3
        pointer-coarse:py-2 pointer-coarse:text-sm`,
        className,
      )}
      {...props}
    />
  )
}

function ComboboxCollection({ ...props }: ComboboxPrimitive.Collection.Props) {
  return (
    <ComboboxPrimitive.Collection data-slot="combobox-collection" {...props} />
  )
}

function ComboboxEmpty({ className, ...props }: ComboboxPrimitive.Empty.Props) {
  return (
    <ComboboxPrimitive.Empty
      data-slot="combobox-empty"
      className={cn(
        `hidden w-full justify-center py-2 text-center text-sm
        text-muted-foreground group-data-empty/combobox-content:flex`,
        className,
      )}
      {...props}
    />
  )
}

function ComboboxSeparator({
  className,
  ...props
}: ComboboxPrimitive.Separator.Props) {
  return (
    <ComboboxPrimitive.Separator
      data-slot="combobox-separator"
      className={cn('-mx-1 my-1 h-px bg-border', className)}
      {...props}
    />
  )
}

function ComboboxChips({
  className,
  ...props
}: React.ComponentPropsWithRef<typeof ComboboxPrimitive.Chips> &
  ComboboxPrimitive.Chips.Props) {
  return (
    <ComboboxPrimitive.Chips
      data-slot="combobox-chips"
      className={cn(
        `flex min-h-9 flex-wrap items-center gap-1.5 rounded-md border
        border-input bg-transparent bg-clip-padding px-2.5 py-1.5 text-sm
        shadow-xs transition-[color,box-shadow] focus-within:border-ring
        focus-within:ring-[3px] focus-within:ring-ring/50
        has-aria-invalid:border-destructive has-aria-invalid:ring-[3px]
        has-aria-invalid:ring-destructive/20
        has-data-[slot=combobox-chip]:px-1.5 dark:bg-input/30`,
        className,
      )}
      {...props}
    />
  )
}

function ComboboxChip({
  className,
  children,
  showRemove = true,
  ...props
}: ComboboxPrimitive.Chip.Props & {
  showRemove?: boolean
}) {
  return (
    <ComboboxPrimitive.Chip
      data-slot="combobox-chip"
      className={cn(
        `flex h-[calc(--spacing(5.5))] w-fit items-center justify-center gap-1
        rounded-sm bg-muted px-1.5 text-xs font-medium whitespace-nowrap
        text-foreground has-disabled:pointer-events-none
        has-disabled:cursor-not-allowed has-disabled:opacity-50
        has-data-[slot=combobox-chip-remove]:pr-0 border-foreground border
        shadow`,
        className,
      )}
      {...props}
    >
      {children}
      {showRemove && (
        <ComboboxPrimitive.ChipRemove
          render={<Button variant="ghost" size="icon-xs" />}
          className="-ml-1 opacity-50 hover:opacity-100"
          data-slot="combobox-chip-remove"
        >
          <LuX className="pointer-events-none" />
        </ComboboxPrimitive.ChipRemove>
      )}
    </ComboboxPrimitive.Chip>
  )
}

function ComboboxChipsInput({
  className,
  // Adding this because ShadCN put this arg in here but it isn't actually used in the component.
  // eslint-disable-next-line unused-imports/no-unused-vars
  children,
  ...props
}: ComboboxPrimitive.Input.Props) {
  return (
    <ComboboxPrimitive.Input
      data-slot="combobox-chip-input"
      className={cn('min-w-16 flex-1 outline-none', className)}
      {...props}
    />
  )
}

function useComboboxAnchor() {
  return React.useRef<HTMLDivElement | null>(null)
}

export {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxClear,
  ComboboxCollection,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxLabel,
  ComboboxList,
  ComboboxSeparator,
  ComboboxTrigger,
  ComboboxValue,
  // ShadCN put this here, so ignoring the error
  // eslint-disable-next-line react-refresh/only-export-components
  useComboboxAnchor,
}
